import { useParams, Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  handleGetAllDishesOfCity,
  handleGetAllChefs,
} from "../../services/get_without_auth";
import DateFilter from "../components/AllDishesDetail/DateFilter";
import AllCuisinesFilter from "../components/AllDishesDetail/AllCuisinesFilter";
import DietaryFilter from "../components/AllDishesDetail/DietaryFilter";
import isValidURL from "../../ValidateUrl";
import { shefDetailPath } from "../../shefPath";

const SearchResults = () => {
  const { term: rawTerm } = useParams();
  const term = rawTerm || "";
  const [searchParams] = useSearchParams();
  const [dishes, setDishes] = useState([]);
  const [chefs, setChefs] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [filterActiveTab, setFilterActiveTab] = useState(0); // 1=Date, 2=Cuisine, 3=Dietary

  // radius filter comes from the header search bar via URL query params
  const lat = parseFloat(searchParams.get("lat"));
  const lng = parseFloat(searchParams.get("lng"));
  const radius = parseFloat(searchParams.get("radius"));
  const radiusFilter =
    radius > 0 && !isNaN(lat) && !isNaN(lng) ? { lat, lng, radius } : null;

  useEffect(() => {
    const city = JSON.parse(localStorage.getItem("region"));
    const fetchResults = async () => {
      try {
        setIsFetching(true);
        const params =
          radiusFilter !== null
            ? {
                lat: radiusFilter.lat,
                lng: radiusFilter.lng,
                radius: radiusFilter.radius,
              }
            : {};

        const [allDishes, matchedChefs] = await Promise.all([
          handleGetAllDishesOfCity(city?.id, {
            ...params,
            ...(term ? { search: term } : {}),
          }),
          handleGetAllChefs(city?.id, {
            ...params,
            ...(term ? { search: term } : {}),
          }),
        ]);

        const keyword = term.toLowerCase();
        let filtered = term
          ? allDishes.filter((dish) =>
              dish.name?.toLowerCase().includes(keyword)
            )
          : allDishes;
        const currentUser = JSON.parse(localStorage.getItem("user"));
        if (currentUser && currentUser.id) {
          filtered = filtered.filter((dish) => dish.user_id !== currentUser.id);
        }
        setDishes(filtered);
        setChefs(matchedChefs || []);
      } catch (err) {
        console.error("Error fetching search results:", err);
      } finally {
        setIsFetching(false);
      }
    };
    fetchResults();
  }, [term, searchParams]);

  return (
    <>
      <Header />
      <div className="container mx-auto my-8 px-lg-2 px-4">
        {/* Filter Tabs */}
        <div>
          {filterActiveTab === 1 && (
            <div className="py-3 border-b">
              <DateFilter />
            </div>
          )}
          {filterActiveTab === 2 && (
            <div className="py-3 border-b">
              <AllCuisinesFilter />
            </div>
          )}
          {filterActiveTab === 3 && (
            <div className="py-3 border-b">
              <DietaryFilter />
            </div>
          )}
        </div>

        {/* Search Results */}
        <div className="mt-8">
          <h2 className="text-secondary font-bold text-2xl mb-0 border-b pb-2">
            {term ? `Search results for: "${term}"` : "All dishes & shefs"}
            {radiusFilter && (
              <span className="text-base font-medium text-gray-500 ml-2">
                within {radiusFilter.radius} km of you
              </span>
            )}
          </h2>

          {isFetching ? (
            <p className="mt-4">Loading...</p>
          ) : dishes.length > 0 || chefs.length > 0 ? (
            <>
            {/* Matching Shefs */}
            {chefs.length > 0 && (
              <div className="mt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Shefs</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {chefs.map((chef) => (
                    <Link
                      key={chef.id}
                      to={shefDetailPath(chef.id, chef.first_name, chef.last_name)}
                      className="flex items-center gap-4 bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-shadow"
                    >
                      <img
                        src={
                          chef.profile_pic && isValidURL(chef.profile_pic)
                            ? chef.profile_pic
                            : "./media/frontend/img/banner/chef-5.webp"
                        }
                        className="img-fluid object-cover h-[60px] w-[60px] object-top rounded-full"
                        alt="Chef"
                      />
                      <div>
                        <h4 className="font-bold text-base">
                          {`${chef.first_name || ""} ${chef.last_name || ""}`}
                        </h4>
                        <p className="text-sm text-gray-500">Professional Shef</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Matching Dishes */}
            {dishes.length > 0 && (
            <>
            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-2">Dishes</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-6">
              {dishes.map((dish) => (
                <div
                  key={dish.id}
                  className="bg-white/90 backdrop-blur-md rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden flex flex-col group border-2 border-gray-100 hover:border-red-200 relative"
                >
                  {/* Decorative Elements */}
                  <div className="absolute top-6 right-6 w-5 h-5 bg-gradient-to-r from-red-500/40 to-red-600/30 rounded-full z-10 animate-pulse"></div>
                  <div className="absolute top-12 right-12 w-3 h-3 bg-red-400/50 rounded-full z-10 animate-pulse delay-300"></div>
                  <div className="absolute bottom-6 left-6 w-4 h-4 bg-red-300/40 rounded-full z-10 animate-pulse delay-700"></div>
                  <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-500 to-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={
                        dish.logo && isValidURL(dish.logo)
                          ? dish.logo
                          : "https://t4.ftcdn.net/jpg/04/70/29/97/360_F_470299797_UD0eoVMMSUbHCcNJCdv2t8B2g1GVqYgs.jpg"
                      }
                      alt={dish.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <Link
                        to={`/dish-detail-single/${dish.id}`}
                        className="text-white bg-red-500/90 backdrop-blur-sm px-6 py-3 rounded-full flex items-center space-x-2 font-semibold shadow-lg transform -translate-y-4 group-hover:translate-y-0 transition-transform duration-300"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="9" cy="21" r="1"></circle>
                          <circle cx="20" cy="21" r="1"></circle>
                          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                        </svg>
                        <span className="text-white">Quick Add</span>
                      </Link>
                    </div>
                    <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg">
                      <span className="text-red-600 font-bold text-lg">
                        {(
                          dish.chef_earning_fee +
                          dish.platform_price +
                          dish.delivery_price
                        ).toLocaleString("en-PK", {
                          style: "currency",
                          currency: "PKR",
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8 flex flex-col flex-grow relative">
                    <div className="mb-4">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2 truncate">
                        <Link to={`/dish-detail-single/${dish.id}`}>
                          {dish.name}
                        </Link>
                      </h3>
                      <p className="text-gray-500 font-medium">
                        by{" "}
                        <Link
                          to={shefDetailPath(dish.user_id, dish?.user?.first_name, dish?.user?.last_name)}
                          className="text-red-600 font-semibold"
                        >
                          {`${dish?.user?.first_name || ""} ${dish?.user?.last_name || ""}`}
                        </Link>
                      </p>
                    </div>

                    {/* Rating */}
                    <div className="flex items-center space-x-2 mb-6">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="#f59e0b"
                        stroke="#f59e0b"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                      </svg>
                      <span className="font-bold text-gray-700 text-lg">
                        {dish?.average_rating
                          ? parseFloat(dish.average_rating).toFixed(1)
                          : 0}
                      </span>
                      <span className="text-gray-500">
                        ({dish?.total_reviews || 0} reviews)
                      </span>
                    </div>

                    {/* Discount */}
                    {dish?.auto_applied_discounts?.length > 0 && (
                      <div className="mb-3 text-sm text-green-600 font-medium">
                        [
                        {dish.auto_applied_discounts[0].discount_type === "$"
                          ? dish.auto_applied_discounts[0].discount.toLocaleString("en-PK", {
                              style: "currency",
                              currency: "PKR",
                            })
                          : (
                              dish.chef_earning_fee *
                              (dish.auto_applied_discounts[0].discount / 100)
                            ).toLocaleString("en-PK", {
                              style: "currency",
                              currency: "PKR",
                            })}
                        <span className="text-xs ml-1">Off</span>]
                      </div>
                    )}

                    {/* Availability */}
                    <div className="mt-auto">
                      <p className="text-sm font-bold text-gray-400 uppercase mb-3 tracking-wider">
                        Available this week
                      </p>
                      <div className="flex justify-between">
                        {[
                          { day: "S", key: "is_sunday" },
                          { day: "M", key: "is_monday" },
                          { day: "T", key: "is_tuesday" },
                          { day: "W", key: "is_wednesday" },
                          { day: "T", key: "is_thursday" },
                          { day: "F", key: "is_friday" },
                          { day: "S", key: "is_saturday" }
                        ].map(({ day, key }, i) => (
                          <div
                            key={i}
                            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                              dish[key] === 1
                                ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg scale-110"
                                : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                            }`}
                          >
                            {day}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </>
            )}
            </>
          ) : (
            <p className="mt-4">No results found.</p>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SearchResults;
